import React from 'react';

// Shared schedule editor used by the editor's bottom ScheduleBar, the
// Agents-page settings gear, the workspace-agent rules, canvas emails and the
// Workspace Preferences sync schedules. The browser timezone is always folded
// in so the server fires at the correct local time.
//
// Shapes (server: services/cognitive-flows/scheduleValidation.js):
//   daily    { type, time, timezone }
//   weekly   { type, time, days, timezone }
//   monthly  { type, time, dayOfMonth: 1–28, timezone }
//   interval { type, every, unit: 'hours' | 'days', startDate: 'YYYY-MM-DD', time, timezone }

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Mo', tue: 'Tu', wed: 'We', thu: 'Th', fri: 'Fr', sat: 'Sa', sun: 'Su' };

export const INTERVAL_LIMITS = { hours: { min: 1, max: 168 }, days: { min: 1, max: 365 } };
const DAY_OF_MONTH_MAX = 28;

// Detect the user's local timezone once (stable across renders).
export const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Today in the browser's zone as YYYY-MM-DD (the default interval start). */
export function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function ordinal(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
}

function intervalPhrase(schedule) {
  const every = Number(schedule.every) || 1;
  const unit = schedule.unit === 'days' ? 'day' : 'hour';
  return every === 1 ? `Every ${unit}` : `Every ${every} ${unit}s`;
}

export function scheduleSummaryText(schedule = {}) {
  const type = schedule.type || 'off';
  const time = schedule.time || '09:00';
  const days = schedule.days || [];
  const tz = schedule.timezone || BROWSER_TZ;
  if (type === 'off') return null;
  if (type === 'daily') return `Runs daily at ${time} (${tz})`;
  if (type === 'weekly' && days.length > 0) return `Runs ${days.map((d) => DAY_LABELS[d]).join(', ')} at ${time} (${tz})`;
  if (type === 'monthly' && schedule.dayOfMonth) return `Runs monthly on the ${ordinal(Number(schedule.dayOfMonth))} at ${time} (${tz})`;
  if (type === 'interval' && schedule.startDate) {
    return `${intervalPhrase(schedule)}, starting ${schedule.startDate} at ${time} (${tz})`;
  }
  return null;
}

// Compact variant for the list badges (cards + table rows): "Daily at 09:00" /
// "Mo, We at 09:00" / "Every 6 hours". The timezone is appended only when it
// differs from the viewer's — the common same-tz case must stay short enough
// for nowrap cells. Null for off and for a dayless weekly (which the scheduler
// never fires).
export function scheduleBadgeText(schedule = {}) {
  const type = schedule.type || 'off';
  const time = schedule.time || '09:00';
  const days = schedule.days || [];
  const tz = schedule.timezone && schedule.timezone !== BROWSER_TZ ? ` (${schedule.timezone})` : '';
  if (type === 'daily') return `Daily at ${time}${tz}`;
  if (type === 'weekly' && days.length > 0) return `${days.map((d) => DAY_LABELS[d]).join(', ')} at ${time}${tz}`;
  if (type === 'monthly' && schedule.dayOfMonth) return `Monthly, ${ordinal(Number(schedule.dayOfMonth))} at ${time}${tz}`;
  if (type === 'interval' && schedule.startDate) {
    return schedule.unit === 'days' ? `${intervalPhrase(schedule)} at ${time}${tz}` : `${intervalPhrase(schedule)} from ${time}${tz}`;
  }
  return null;
}

/** True when a non-off schedule has everything the server needs to fire it. */
export function isScheduleComplete(schedule = {}) {
  const type = schedule.type || 'off';
  if (type === 'off' || type === 'daily') return true;
  if (type === 'weekly') return (schedule.days || []).length > 0;
  if (type === 'monthly') return Number(schedule.dayOfMonth) >= 1 && Number(schedule.dayOfMonth) <= DAY_OF_MONTH_MAX;
  if (type === 'interval') {
    const lim = INTERVAL_LIMITS[schedule.unit];
    const every = Number(schedule.every);
    return !!lim && Number.isInteger(every) && every >= lim.min && every <= lim.max && /^\d{4}-\d{2}-\d{2}$/.test(schedule.startDate || '');
  }
  return false;
}

const s = {
  select: { padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px', background: '#fff', cursor: 'pointer' },
  timeInput: { padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px', width: '110px' },
  dateInput: { padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px', width: '150px' },
  numInput: { padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px', width: '72px' },
  label: { fontSize: '12.5px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' },
  group: { display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  dayBtn: (active) => ({
    padding: '4px 9px', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
    background: active ? 'var(--accent-light)' : '#fff',
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
  }),
};

/**
 * @param schedule { type, time, days, timezone, dayOfMonth?, every?, unit?, startDate? }
 * @param onChange (nextSchedule) => void   — always receives the full schedule object
 * @param layout   'inline' (default) | 'stack'
 */
export default function ScheduleFields({ schedule = {}, onChange, layout = 'inline' }) {
  const type = schedule.type || 'off';
  const time = schedule.time || '09:00';
  const days = schedule.days || [];
  const timezone = schedule.timezone || BROWSER_TZ;

  const update = (patch) => onChange({ ...schedule, timezone, ...patch });
  const toggleDay = (day) => {
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    update({ days: next });
  };
  const changeType = (next) => {
    const patch = { type: next };
    // Seed the fields a new type needs so the first save is already complete.
    if (next === 'monthly' && !schedule.dayOfMonth) patch.dayOfMonth = 1;
    if (next === 'interval') {
      if (!schedule.every) patch.every = 6;
      if (!schedule.unit) patch.unit = 'hours';
      if (!schedule.startDate) patch.startDate = todayIso();
    }
    update(patch);
  };
  const changeEvery = (raw) => {
    if (raw === '') { update({ every: '' }); return; }
    const lim = INTERVAL_LIMITS[schedule.unit || 'hours'];
    const n = Math.round(Number(raw));
    if (!Number.isFinite(n)) return;
    update({ every: Math.min(lim.max, Math.max(lim.min, n)) });
  };
  const changeUnit = (unit) => {
    const lim = INTERVAL_LIMITS[unit];
    const every = Math.min(lim.max, Math.max(lim.min, Number(schedule.every) || 1));
    update({ unit, every });
  };

  const wrap = layout === 'stack'
    ? { display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }
    : { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' };

  return (
    <div style={wrap}>
      <select style={s.select} value={type} onChange={(e) => changeType(e.target.value)} aria-label="Repeat">
        <option value="off">Off</option>
        <option value="daily">Every Day</option>
        <option value="weekly">Every Week</option>
        <option value="monthly">Every Month</option>
        <option value="interval">Every X Hours / Days</option>
      </select>

      {type === 'interval' && (
        <span style={s.group}>
          <span style={s.label}>Every</span>
          <input
            style={s.numInput}
            type="number"
            min={INTERVAL_LIMITS[schedule.unit || 'hours'].min}
            max={INTERVAL_LIMITS[schedule.unit || 'hours'].max}
            value={schedule.every ?? ''}
            onChange={(e) => changeEvery(e.target.value)}
            aria-label="Repeat every"
          />
          <select style={s.select} value={schedule.unit || 'hours'} onChange={(e) => changeUnit(e.target.value)} aria-label="Interval unit">
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
        </span>
      )}

      {type === 'monthly' && (
        <span style={s.group}>
          <span style={s.label}>On day</span>
          <select
            style={s.select}
            value={schedule.dayOfMonth || 1}
            onChange={(e) => update({ dayOfMonth: Number(e.target.value) })}
            aria-label="Day of the month"
          >
            {Array.from({ length: DAY_OF_MONTH_MAX }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>{ordinal(d)}</option>
            ))}
          </select>
        </span>
      )}

      {type === 'interval' && (
        <span style={s.group}>
          <span style={s.label}>Starting</span>
          <input
            style={s.dateInput}
            type="date"
            value={schedule.startDate || ''}
            onChange={(e) => update({ startDate: e.target.value })}
            aria-label="Start date"
          />
        </span>
      )}

      {type !== 'off' && (
        <span style={s.group}>
          {(type === 'interval' || type === 'monthly') && <span style={s.label}>at</span>}
          <input style={s.timeInput} type="time" value={time} onChange={(e) => update({ time: e.target.value })} aria-label="Time" />
        </span>
      )}

      {type === 'weekly' && (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {DAYS.map((d) => (
            <button key={d} type="button" style={s.dayBtn(days.includes(d))} onClick={() => toggleDay(d)}>
              {DAY_LABELS[d]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
